// Canvas2D timing renderer — renders the admin lesson timing editor.
// Mirrors the coordinate math from player/src/renderer.rs but adds editing overlays.

import { TimingState, laneY, laneIndexOf, NotePayload } from "./timing_state"

const GUTTER = 64
const STRIP_H = 0 // no input strip in editor
const PLAYHEAD_FRACTION = 0.24
const LOOK_AHEAD_DEFAULT = 16 // beats visible ahead of playhead

interface Tokens {
  brand: string
  green: string
  amber: string
  red: string
  blue: string
  purple: string
  bg: string
  bg2: string
  panel: string
  chip: string
  track: string
  line: string
  line2: string
  lineStrong: string
  fg: string
  fg2: string
  fg3: string
  fg4: string
}

const LANE_COLORS = ["--brand", "--blue", "--green", "--amber", "--purple", "--red"]

function readTokens(): Tokens {
  const s = getComputedStyle(document.documentElement)
  const g = (name: string) => s.getPropertyValue(name).trim() || "#888"
  return {
    brand: g("--brand"),
    green: g("--green"),
    amber: g("--amber"),
    red: g("--red"),
    blue: g("--blue"),
    purple: g("--purple"),
    bg: g("--bg"),
    bg2: g("--bg-2"),
    panel: g("--panel"),
    chip: g("--chip-bg"),
    track: g("--track"),
    line: g("--line"),
    line2: g("--line-2"),
    lineStrong: g("--line-strong"),
    fg: g("--fg"),
    fg2: g("--fg-2"),
    fg3: g("--fg-3"),
    fg4: g("--fg-4"),
  }
}

function laneColor(laneIdx: number, tokens: Tokens): string {
  const vars = [tokens.brand, tokens.blue, tokens.green, tokens.amber, tokens.purple, tokens.red]
  return vars[laneIdx % vars.length]
}

function padLabel(name: string): string {
  const map: Record<string, string> = {
    kick: "KCK", snare: "SNA", clap: "CLP", hihat: "HAT", hat: "HAT",
    openhat: "OH", rim: "RIM", perc: "PRC", tom: "TOM", crash: "CRS",
    ride: "RIDE", shaker: "SHK", tamb: "TMB",
  }
  return map[name.toLowerCase()] || name.substring(0, 3).toUpperCase()
}

function noteLabel(noteName: string, instrument: string): string {
  if (instrument === "drums") return padLabel(noteName)
  return noteName // pitch names like "C4", "Eb3"
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  r = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.arcTo(x + w, y, x + w, y + r, r)
  ctx.lineTo(x + w, y + h - r)
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
  ctx.lineTo(x + r, y + h)
  ctx.arcTo(x, y + h, x, y + h - r, r)
  ctx.lineTo(x, y + r)
  ctx.arcTo(x, y, x + r, y, r)
  ctx.closePath()
}

export interface TimingRendererOptions {
  canvas: HTMLCanvasElement
  instrument: string
}

export class TimingRenderer {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private dpr: number
  private tokens: Tokens
  private instrument: string
  private animFrame: number | null = null
  private beat: number = 0
  private lookAhead: number = LOOK_AHEAD_DEFAULT

  // Marquee rectangle in canvas coords (set by controller, cleared after render)
  marqueeRect: { x1: number; y1: number; x2: number; y2: number } | null = null

  constructor(opts: TimingRendererOptions) {
    this.canvas = opts.canvas
    this.ctx = this.canvas.getContext("2d")!
    this.dpr = window.devicePixelRatio || 1
    this.tokens = readTokens()
    this.instrument = opts.instrument

    this.resize()
    new MutationObserver(() => {
      this.tokens = readTokens()
    }).observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] })
  }

  destroy(): void {
    if (this.animFrame !== null) cancelAnimationFrame(this.animFrame)
  }

  resize(): void {
    const rect = this.canvas.parentElement?.getBoundingClientRect()
    if (!rect) return
    const w = rect.width
    const h = rect.height
    this.canvas.width = w * this.dpr
    this.canvas.height = h * this.dpr
    this.canvas.style.width = w + "px"
    this.canvas.style.height = h + "px"
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
  }

  setBeat(b: number): void {
    this.beat = b
  }

  getBeat(): number {
    return this.beat
  }

  setLookAhead(beats: number): void {
    this.lookAhead = Math.max(4, Math.min(64, beats))
  }

  getLookAhead(): number {
    return this.lookAhead
  }

  // Beat → screen X
  private beatToX(beat: number, playheadX: number, pxPerBeat: number): number {
    return playheadX + (beat - this.beat) * pxPerBeat
  }

  // Screen X → beat
  xToBeat(x: number, playheadX: number, pxPerBeat: number): number {
    return this.beat + (x - playheadX) / pxPerBeat
  }

  // Hit test: returns note index or -1
  hitTest(x: number, y: number, state: TimingState, canvasW: number, canvasH: number): number {
    const timelineH = canvasH - STRIP_H
    const laneCount = state.lanes.length
    const rowH = timelineH / Math.max(laneCount, 1)
    const playheadX = GUTTER + (canvasW - GUTTER) * PLAYHEAD_FRACTION
    const pxPerBeat = (canvasW - playheadX) / this.lookAhead
    const radius = Math.min(rowH * 0.32, 16)

    for (let i = state.notes.length - 1; i >= 0; i--) {
      const n = state.notes[i]
      const laneIdx = laneIndexOf(n.note, state.lanes)
      const nx = this.beatToX(n.beat, playheadX, pxPerBeat)
      const ny = laneY(laneIdx, laneCount, timelineH) + rowH / 2
      const tailW = Math.max(0, n.duration * pxPerBeat)

      // Check note circle or tail
      const dx = x - nx
      const dy = y - ny
      if (dx * dx + dy * dy <= (radius + 4) * (radius + 4)) return i
      if (dx >= 0 && dx <= tailW + radius && Math.abs(dy) <= radius) return i
    }
    return -1
  }

  // Hit test right edge of note for resize
  hitTestEdge(x: number, y: number, state: TimingState, canvasW: number, canvasH: number): number {
    const timelineH = canvasH - STRIP_H
    const laneCount = state.lanes.length
    const rowH = timelineH / Math.max(laneCount, 1)
    const playheadX = GUTTER + (canvasW - GUTTER) * PLAYHEAD_FRACTION
    const pxPerBeat = (canvasW - playheadX) / this.lookAhead
    const radius = Math.min(rowH * 0.32, 16)

    for (let i = state.notes.length - 1; i >= 0; i--) {
      const n = state.notes[i]
      const laneIdx = laneIndexOf(n.note, state.lanes)
      const nx = this.beatToX(n.beat, playheadX, pxPerBeat)
      const ny = laneY(laneIdx, laneCount, timelineH) + rowH / 2
      const tailEnd = nx + Math.max(0, n.duration * pxPerBeat)

      // Check if near right edge
      if (Math.abs(x - tailEnd) <= 6 && Math.abs(y - ny) <= radius) return i
    }
    return -1
  }

  // Lane at screen Y
  laneAtY(y: number, state: TimingState, canvasH: number): string {
    const timelineH = canvasH - STRIP_H
    const laneCount = state.lanes.length
    const rowH = timelineH / Math.max(laneCount, 1)
    const idx = Math.floor((timelineH - y) / rowH)
    return state.lanes[Math.max(0, Math.min(idx, laneCount - 1))] || state.lanes[0]
  }

  render(state: TimingState): void {
    const w = this.canvas.width / this.dpr
    const h = this.canvas.height / this.dpr
    const ctx = this.ctx
    const t = this.tokens
    const timelineH = h - STRIP_H
    const laneCount = state.lanes.length
    const rowH = timelineH / Math.max(laneCount, 1)
    const playheadX = GUTTER + (w - GUTTER) * PLAYHEAD_FRACTION
    const pxPerBeat = (w - playheadX) / this.lookAhead

    // Clear
    ctx.fillStyle = t.bg
    ctx.fillRect(0, 0, w, h)

    // Row stripes
    for (let i = 0; i < laneCount; i++) {
      const ly = laneY(i, laneCount, timelineH)
      if (i % 2 === 1) {
        ctx.fillStyle = t.track
        ctx.fillRect(GUTTER, ly, w - GUTTER, rowH)
      }
    }

    // Row dividers
    ctx.strokeStyle = t.line
    ctx.lineWidth = 1
    for (let i = 0; i <= laneCount; i++) {
      const ly = laneY(i, laneCount, timelineH)
      ctx.beginPath()
      ctx.moveTo(GUTTER, ly)
      ctx.lineTo(w, ly)
      ctx.stroke()
    }

    // Beat + bar lines
    const firstBeat = Math.floor(this.beat - this.lookAhead * 0.3)
    const lastBeat = Math.ceil(this.beat + this.lookAhead)
    for (let b = firstBeat; b <= lastBeat; b++) {
      const x = this.beatToX(b, playheadX, pxPerBeat)
      if (x < GUTTER || x > w) continue

      const isBar = (b - 1) % state.timeSignatureTop === 0 && b >= 1
      ctx.strokeStyle = isBar ? t.lineStrong : t.line
      ctx.lineWidth = isBar ? 1.5 : 1
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x, timelineH)
      ctx.stroke()

      if (isBar && b >= 1) {
        ctx.fillStyle = t.fg3
        ctx.font = "10px var(--mono)"
        ctx.textAlign = "center"
        ctx.fillText(String(Math.ceil((b - 1) / state.timeSignatureTop) + 1), x, 10)
      }
    }

    // Notes
    const radius = Math.min(rowH * 0.32, 16)
    for (let i = 0; i < state.notes.length; i++) {
      const n = state.notes[i]
      const laneIdx = laneIndexOf(n.note, state.lanes)
      const x = this.beatToX(n.beat, playheadX, pxPerBeat)
      const cy = laneY(laneIdx, laneCount, timelineH) + rowH / 2

      if (x < GUTTER - 60 || x > w + 60) continue

      const color = laneColor(laneIdx, t)
      const isSelected = state.selectedIndices.has(i)
      const behindPlayhead = n.beat < this.beat

      // Duration tail
      const tailW = Math.max(0, n.duration * pxPerBeat - radius)
      if (tailW > 0) {
        ctx.globalAlpha = isSelected ? 0.5 : behindPlayhead ? 0.15 : 0.35
        ctx.fillStyle = color
        roundRectPath(ctx, x, cy - radius * 0.45, radius + tailW, radius * 0.9, radius * 0.45)
        ctx.fill()
        ctx.globalAlpha = 1
      }

      // Note dot
      ctx.globalAlpha = behindPlayhead && !isSelected ? 0.3 : 1
      ctx.fillStyle = isSelected ? t.fg : color
      ctx.beginPath()
      ctx.arc(x, cy, radius, 0, Math.PI * 2)
      ctx.fill()

      // Selection ring
      if (isSelected) {
        ctx.strokeStyle = t.brand
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.arc(x, cy, radius + 3, 0, Math.PI * 2)
        ctx.stroke()
      }

      // Resize handle (only on single selection)
      if (isSelected && state.selectedIndices.size === 1 && tailW > 4) {
        ctx.fillStyle = t.fg
        const handleX = x + tailW + radius - 2
        roundRectPath(ctx, handleX, cy - 3, 4, 6, 2)
        ctx.fill()
      }

      // Label
      ctx.globalAlpha = behindPlayhead && !isSelected ? 0.3 : 1
      ctx.fillStyle = "#fff"
      ctx.font = `${Math.min(radius * 0.9, 11)}px var(--mono)`
      ctx.textAlign = "center"
      ctx.textBaseline = "middle"
      ctx.fillText(noteLabel(n.note, this.instrument), x + (tailW > 0 ? tailW / 2 + radius / 2 : 0), cy)

      ctx.globalAlpha = 1
    }

    // Gutter background
    ctx.fillStyle = t.panel
    ctx.fillRect(0, 0, GUTTER, h)

    // Lane labels in gutter
    for (let i = 0; i < laneCount; i++) {
      const ly = laneY(i, laneCount, timelineH)
      const cy = ly + rowH / 2
      const color = laneColor(i, t)

      ctx.fillStyle = color
      ctx.beginPath()
      ctx.arc(12, cy, 4, 0, Math.PI * 2)
      ctx.fill()

      ctx.fillStyle = t.fg2
      ctx.font = "11px var(--mono)"
      ctx.textAlign = "left"
      ctx.textBaseline = "middle"
      const label = state.lanes[i].length > 6 ? state.lanes[i].substring(0, 6) : state.lanes[i]
      ctx.fillText(label, 22, cy)
    }

    // Gutter dividers
    ctx.strokeStyle = t.line2
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(GUTTER, 0)
    ctx.lineTo(GUTTER, h)
    ctx.stroke()

    // Playhead
    ctx.strokeStyle = t.brand
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(playheadX, 0)
    ctx.lineTo(playheadX, timelineH)
    ctx.stroke()

    // Playhead triangle
    ctx.fillStyle = t.brand
    ctx.beginPath()
    ctx.moveTo(playheadX - 7, 0)
    ctx.lineTo(playheadX + 7, 0)
    ctx.lineTo(playheadX, 10)
    ctx.closePath()
    ctx.fill()

    // Snap grid lines
    if (state.snap > 0) {
      ctx.globalAlpha = 0.15
      ctx.strokeStyle = t.fg3
      ctx.lineWidth = 0.5
      const snapInterval = state.snap
      const firstSnap = Math.floor(this.beat / snapInterval) * snapInterval
      for (let s = firstSnap; s <= this.beat + this.lookAhead; s += snapInterval) {
        const x = this.beatToX(s, playheadX, pxPerBeat)
        if (x < GUTTER || x > w) continue
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, timelineH)
        ctx.stroke()
      }
      ctx.globalAlpha = 1
    }

    // Marquee selection rectangle
    if (this.marqueeRect) {
      const mr = this.marqueeRect
      const rx = Math.min(mr.x1, mr.x2)
      const ry = Math.min(mr.y1, mr.y2)
      const rw = Math.abs(mr.x2 - mr.x1)
      const rh = Math.abs(mr.y2 - mr.y1)
      ctx.strokeStyle = t.brand
      ctx.lineWidth = 1
      ctx.setLineDash([4, 3])
      ctx.strokeRect(rx, ry, rw, rh)
      ctx.fillStyle = "rgba(255,153,51,0.08)"
      ctx.fillRect(rx, ry, rw, rh)
      ctx.setLineDash([])
    }
  }
}
